/**
 * Every backend in this estate returns errors as {"error": "..."} with the HTTP
 * status carrying the semantics. There is no response envelope on success.
 */
export interface ApiErrorBody {
  error: string;
  retry_after?: number;
}

export type ApiFailureKind =
  /** No credential presented, or it expired. The user must sign in again. */
  | "unauthenticated"
  /** Credential is fine and the answer is no — role or tenant containment. */
  | "forbidden"
  /** The request itself was malformed or named no scope. */
  | "bad-request"
  /** The gateway's bulkhead is full. Transient; retry. */
  | "at-capacity"
  /** Route not published at the edge, or no such report. */
  | "not-found"
  /** Anything else, including transport failures. */
  | "unavailable"
  /**
   * The request was never sent, because the client refused to send it.
   * See gateway.ts — no token or no namespace means no request.
   */
  | "fail-closed";

export class ApiError extends Error {
  readonly kind: ApiFailureKind;
  readonly status: number | null;
  readonly retryAfter: number | null;
  /**
   * The parsed error body, when there was one. Some refusals carry more than
   * `error` -- radcustomer's roster upload lists the refused line of the file
   * in `rows` -- and a screen that drops it can only say "the file has errors".
   */
  readonly body: unknown;

  constructor(
    kind: ApiFailureKind,
    message: string,
    status: number | null = null,
    retryAfter: number | null = null,
    body: unknown = null,
  ) {
    super(message);
    this.name = "ApiError";
    this.kind = kind;
    this.status = status;
    this.retryAfter = retryAfter;
    this.body = body;
  }
}

/**
 * 🔑 401 and 403 must stay distinguishable all the way to the UI.
 *
 * Two independent gates in this stack return a byte-identical 403 — the role
 * check and the namespace containment check — and only the server's log line
 * separates them. Collapsing 401 and 403 into one "access denied" toast throws
 * away the only signal the operator has about which one they hit, and sends
 * them to re-login for a problem that re-login cannot fix.
 */
export function classify(status: number): ApiFailureKind {
  if (status === 401) return "unauthenticated";
  if (status === 403) return "forbidden";
  if (status === 400) return "bad-request";
  if (status === 404) return "not-found";
  if (status === 429 || status === 503) return "at-capacity";
  return "unavailable";
}
